import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Layout, StatusBadge, AudioPlayer, Modal, PhoneNumber } from '../components';
import type { Lead, StatusHistory, User } from '../types';
import { PROPERTY_TYPE_LABELS } from '../types';
import { formatDateTime, formatCurrency } from '../utils/date';
import { leadsApi, authApi } from '../utils/api';

export function LeadDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const [user, setUser] = useState<User | null>(null);
  const [lead, setLead] = useState<Lead | null>(null);
  const [statusHistory, setStatusHistory] = useState<StatusHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDisqualifyModal, setShowDisqualifyModal] = useState(false);
  const [disqualifyReason, setDisqualifyReason] = useState('');
  const [updating, setUpdating] = useState(false);

  // Check authentication
  useEffect(() => {
    const stored = localStorage.getItem('rewagent_user');
    if (stored) {
      setUser(JSON.parse(stored));
    } else {
      navigate('/');
    }
  }, [navigate]);

  // Fetch lead details
  useEffect(() => {
    const fetchLead = async () => {
      if (!id) return;

      setLoading(true);
      setError(null);

      try {
        const data = await leadsApi.get(id);
        setLead(data.lead);
        setStatusHistory(data.status_history || []);
      } catch (err) {
        if (err instanceof Error && err.message.includes('401')) {
          localStorage.removeItem('rewagent_user');
          navigate('/');
          return;
        }
        if (err instanceof Error && err.message.includes('404')) {
          setError('Lead not found');
          return;
        }
        setError(err instanceof Error ? err.message : 'Failed to load lead');
      } finally {
        setLoading(false);
      }
    };

    fetchLead();
  }, [id, navigate]);

  const handleLogout = async () => {
    try {
      await authApi.logout();
    } catch (err) {
      console.error('Logout error:', err);
    }
    localStorage.removeItem('rewagent_user');
    localStorage.removeItem('rewagent_token');
    navigate('/');
  };

  const handleStatusUpdate = async (status: 'qualified' | 'disqualified', reason?: string) => {
    if (!lead) return;

    setUpdating(true);

    try {
      await leadsApi.updateStatus(lead.id, status, reason);

      // Refresh lead data
      const data = await leadsApi.get(lead.id);
      setLead(data.lead);
      setStatusHistory(data.status_history || []);
      setShowDisqualifyModal(false);
      setDisqualifyReason('');
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  // Sell reason labels
  const SELL_REASONS: Record<number, string> = {
    0: 'Not specified',
    1: 'Relocating',
    2: 'Financial difficulties',
    3: 'Inherited property',
    4: 'Divorce',
    5: 'Health issues',
    6: 'Property too large',
    7: 'Property too small',
    8: 'Investment sale',
    9: 'Avoiding foreclosure',
    10: 'Estate sale',
    11: 'Other',
  };

  // Closing timeline labels
  const CLOSING_TIMELINES: Record<number, string> = {
    0: 'Not specified',
    1: 'ASAP (within 30 days)',
    2: '1-3 months',
    3: '3-6 months',
    4: '6-12 months',
    5: '12+ months',
    6: 'No rush',
    7: 'Just exploring',
  };

  if (loading) {
    return (
      <Layout showHeader userName={user?.name} onLogout={handleLogout}>
        <div className="flex items-center justify-center py-24">
          <svg className="animate-spin h-8 w-8 text-blue-600" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
        </div>
      </Layout>
    );
  }

  if (error || !lead) {
    return (
      <Layout showHeader userName={user?.name} onLogout={handleLogout}>
        <div className="max-w-3xl mx-auto px-4 py-12">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <svg className="w-12 h-12 text-red-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h2 className="text-lg font-semibold text-red-800 mb-2">{error || 'Lead not found'}</h2>
            <Link to="/dashboard" className="text-blue-600 hover:text-blue-700 font-medium">
              ← Back to Dashboard
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout showHeader userName={user?.name} onLogout={handleLogout}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back button */}
        <div className="mb-6">
          <Link
            to="/dashboard"
            className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 font-medium"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Dashboard
          </Link>
        </div>

        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {lead.owner_firstname} {lead.owner_lastname}
              </h1>
              <p className="text-gray-600 mt-1">{lead.prop_address}</p>
            </div>
            <StatusBadge status={lead.status} size="lg" />
          </div>
        </div>

        {/* Main content grid */}
        <div className="grid gap-6 lg:grid-cols-2">
          {/* Owner Information */}
          <Section title="Owner Information">
            <InfoRow label="Name" value={`${lead.owner_firstname} ${lead.owner_lastname}`} />
            <div className="flex items-start">
              <span className="text-sm text-gray-500 w-32 flex-shrink-0">Phone:</span>
              <PhoneNumber phone={lead.owner_phone} status={lead.status} />
            </div>
            <InfoRow label="Email" value={lead.owner_email || 'N/A'} />
          </Section>

          {/* Property Details */}
          <Section title="Property Details">
            <InfoRow label="Address" value={lead.prop_address} />
            <InfoRow label="Type" value={PROPERTY_TYPE_LABELS[lead.prop_type]} />
            <InfoRow label="Bedrooms" value={lead.bedrooms?.toString() || 'N/A'} />
            <InfoRow label="Bathrooms" value={lead.bathrooms?.toString() || 'N/A'} />
            <InfoRow label="Condition Score" value={`${lead.cond_scale}/10`} />
            {lead.updates_text && <InfoRow label="Updates" value={lead.updates_text} />}
          </Section>

          {/* Financial Info */}
          <Section title="Financial Information">
            <InfoRow label="Market Value" value={formatCurrency(lead.market_val)} />
            <InfoRow label="Asking Price" value={formatCurrency(lead.ask_price)} />
            <InfoRow label="Mortgage" value={lead.mortgage === 0 ? 'No' : lead.mortgage === 1 ? 'Yes' : 'Unknown'} />
            {lead.mortgage_amount && <InfoRow label="Mortgage Amount" value={formatCurrency(lead.mortgage_amount)} />}
            <InfoRow label="Lead Price" value={formatCurrency(lead.lead_price)} />
          </Section>

          {/* Sale Information */}
          <Section title="Sale Information">
            <InfoRow label="Reason" value={SELL_REASONS[lead.sell_reason] || 'N/A'} />
            <InfoRow label="Timeline" value={CLOSING_TIMELINES[lead.closn_tmln] || 'N/A'} />
            <InfoRow label="Motivation Score" value={`${lead.motivation}/10`} />
            <InfoRow label="Lead Score" value={`${lead.lead_score}/10`} />
          </Section>

          {/* Status & Actions */}
          <Section title="Status & Actions" className="lg:col-span-2">
            <div className="flex items-center space-x-4 mb-4">
              <span className="text-sm text-gray-500">Current Status:</span>
              <StatusBadge status={lead.status} />
            </div>
            
            {lead.status === 'new' && (
              <div className="flex space-x-3">
                <button
                  onClick={() => handleStatusUpdate('qualified')}
                  disabled={updating}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-md shadow-sm transition-colors disabled:opacity-50"
                >
                  Qualify
                </button>
                <button
                  onClick={() => setShowDisqualifyModal(true)}
                  disabled={updating}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-medium rounded-md shadow-sm transition-colors disabled:opacity-50"
                >
                  Disqualify
                </button>
              </div>
            )}

            {lead.disqualify_reason && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
                <span className="text-sm font-medium text-red-700">Disqualify Reason: </span>
                <span className="text-sm text-red-600">{lead.disqualify_reason}</span>
              </div>
            )}
          </Section>

          {/* Recording */}
          {lead.recording_url && (
            <Section title="Recording" className="lg:col-span-2">
              <AudioPlayer url={lead.recording_url} title="Call Recording" />
            </Section>
          )}

          {/* Additional Info */}
          {(lead.info || lead.campaign) && (
            <Section title="Additional Information" className="lg:col-span-2">
              {lead.campaign && <InfoRow label="Campaign" value={`Campaign #${lead.campaign}`} />}
              {lead.info && <InfoRow label="Notes" value={lead.info} />}
            </Section>
          )}

          {/* Status History */}
          {statusHistory.length > 0 && (
            <Section title="Status History" className="lg:col-span-2">
              <div className="space-y-3">
                {statusHistory.map((entry, index) => (
                  <div key={entry.id} className="flex items-start space-x-3">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                      <span className="text-xs font-medium text-gray-500">{index + 1}</span>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <StatusBadge status={entry.new_status as any} size="sm" />
                        <span className="text-sm text-gray-500">
                          {formatDateTime(entry.created_at)}
                        </span>
                      </div>
                      {entry.reason && (
                        <p className="text-sm text-gray-600 mt-1">{entry.reason}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </Section>
          )}
        </div>

        {/* Created/Updated info */}
        <div className="mt-6 text-sm text-gray-500 text-center">
          Created: {formatDateTime(lead.created_at)} • Updated: {formatDateTime(lead.updated_at)}
        </div>
      </div>

      {/* Disqualify Modal */}
      <Modal
        isOpen={showDisqualifyModal}
        onClose={() => setShowDisqualifyModal(false)}
        title="Disqualify Lead"
      >
        <div className="space-y-4">
          <p className="text-gray-600">Please provide a reason for disqualifying this lead.</p>
          <textarea
            value={disqualifyReason}
            onChange={(e) => setDisqualifyReason(e.target.value)}
            placeholder="Enter disqualification reason..."
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            rows={3}
          />
          <div className="flex justify-end space-x-3">
            <button
              onClick={() => setShowDisqualifyModal(false)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => handleStatusUpdate('disqualified', disqualifyReason)}
              disabled={!disqualifyReason.trim() || updating}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors disabled:opacity-50"
            >
              Confirm Disqualify
            </button>
          </div>
        </div>
      </Modal>
    </Layout>
  );
}

// Helper components
function Section({ title, children, className = '' }: { title: string; children: React.ReactNode; className?: string }) {
  return (
    <div className={`bg-white rounded-lg shadow-sm border border-gray-200 p-6 ${className}`}>
      <h2 className="text-lg font-semibold text-gray-900 mb-4">{title}</h2>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="flex items-start">
      <span className="text-sm text-gray-500 w-32 flex-shrink-0">{label}:</span>
      <span className="text-sm text-gray-900">{value || 'N/A'}</span>
    </div>
  );
}
